import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import VangardHeader from "@/components/vangard/VangardHeader";
import VangardFooter from "@/components/vangard/VangardFooter";
import VMark from "@/components/vangard/VMark";
import { FloatingTile, AmbientHalos } from "@/components/vangard/FloatingTile";
import WordCycler from "@/components/vangard/WordCycler";
import Seo, { organizationSchema, websiteSchema } from "@/components/Seo";
import { TIERS, priceOf, FEATURE_ICONS } from "@/data/tiers";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

function Eyebrow({ children, index }) {
  return (
    <div className="flex items-center gap-4 text-neutral-400">
      <span className="text-[11px] tracking-[0.35em] uppercase">{children}</span>
      {index && (
        <>
          <span className="h-px w-10 bg-white/15" />
          <span className="font-serif text-sm text-neutral-500">{index}</span>
        </>
      )}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const clubPillars = [
    { n: "01", title: t("vc.club.p1-title"), body: t("vc.club.p1-body") },
    { n: "02", title: t("vc.club.p2-title"), body: t("vc.club.p2-body") },
    { n: "03", title: t("vc.club.p3-title"), body: t("vc.club.p3-body") },
  ];

  const network = [
    { n: "01", title: t("vc.net.c1-title"), body: t("vc.net.c1-body") },
    { n: "02", title: t("vc.net.c2-title"), body: t("vc.net.c2-body") },
    { n: "03", title: t("vc.net.c3-title"), body: t("vc.net.c3-body") },
    { n: "04", title: t("vc.net.c4-title"), body: t("vc.net.c4-body") },
  ];


  const lifestyle = [
    { title: t("vc.life.travel"), body: t("vc.life.travel-body") },
    { title: t("vc.life.dining"), body: t("vc.life.dining-body") },
    { title: t("vc.life.mobility"), body: t("vc.life.mobility-body") },
    { title: t("vc.life.sourcing"), body: t("vc.life.sourcing-body") },
  ];

  const events = [
    { title: t("vc.event.dinner"), body: t("vc.event.dinner-body") },
    { title: t("vc.event.founder"), body: t("vc.event.founder-body") },
    { title: t("vc.event.expert"), body: t("vc.event.expert-body") },
  ];

  const joinSteps = [
    { n: "01", title: t("vc.join.s1-title"), body: t("vc.join.s1-body") },
    { n: "02", title: t("vc.join.s2-title"), body: t("vc.join.s2-body") },
    { n: "03", title: t("vc.join.s3-title"), body: t("vc.join.s3-body") },
    { n: "04", title: t("vc.join.s4-title"), body: t("vc.join.s4-body") },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="home-page">
      <Seo
        title={t("vc.seo.home.title")}
        description={t("vc.seo.home.desc")}
        schema={[organizationSchema(), websiteSchema()]}
      />
      <VangardHeader />

      {/* ============ Hero ============ */}
      <section className="relative overflow-hidden pt-[92px] h-[100svh] min-h-[640px] flex items-center bg-gradient-to-b from-[#050505] via-[#0A0A0A] to-[#070707]">
        <AmbientHalos />
        <VMark position="top-right" size={720} opacity={0.05} />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-16 py-6 lg:py-10 w-full grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          {/* Vertical spine — book-spine metadata rail */}
          <div
            aria-hidden="true"
            className="hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 -rotate-90 origin-center text-[10px] tracking-[0.55em] uppercase text-neutral-500 whitespace-nowrap"
            style={{ transformOrigin: "left center" }}
          >
            {t("vc.hero.spine")}
          </div>

          {/* Left: editorial title with cycler + numbered rule-line CTAs */}
          <div className="lg:col-span-6 relative z-10 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex items-center gap-4 text-neutral-500"
            >
              <span className="font-serif text-[11px] tracking-[0.35em] uppercase">{t("vc.hero.eyebrow")}</span>
              <span className="h-px w-10 bg-white/20" />
              <span className="font-serif text-neutral-400 text-sm">N°01</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.08 }}
              className="font-sans font-light leading-[0.95] tracking-[-0.03em] mt-8"
              style={{ fontSize: "clamp(2.4rem, 6.2vw, 5.2rem)" }}
            >
              {t("vc.hero.title-1")}
              <br />
              <span className="text-neutral-300">{t("vc.hero.that")} </span>
              <WordCycler
                keys={["vc.hero.cycler-1", "vc.hero.cycler-2", "vc.hero.cycler-3", "vc.hero.cycler-4"]}
                interval={2400}
                className="text-white underline decoration-white/25 decoration-2 underline-offset-8"
                testid="hero-word-cycler"
              />
              <br />
              <span className="text-neutral-300">{t("vc.hero.for-you")}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.18 }}
              className="mt-8 text-[14px] sm:text-[15px] text-neutral-400 font-light max-w-[46ch] leading-relaxed"
            >
              {t("vc.hero.sub")}
            </motion.p>

            {/* Numbered rule-line CTAs — no pill buttons */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="mt-10 flex flex-col divide-y divide-white/[0.08] border-y border-white/[0.08] max-w-[520px]"
            >
              <button
                onClick={() => navigate(user ? "/account/membership" : "/register")}
                className="group flex items-center gap-5 py-4 text-left hover:pl-2 transition-[padding] duration-300"
                data-testid="hero-cta-explore"
              >
                <span className="font-serif text-[10px] tracking-[0.4em] uppercase text-neutral-500 w-6">01</span>
                <span className="flex-1 font-sans font-light text-[22px] text-white transition-all">
                  {t("vc.hero.cta-explore")}
                </span>
                <span className="flex items-center gap-2 text-neutral-500 group-hover:text-white transition-colors">
                  <span className="h-px w-8 bg-current transition-all group-hover:w-14" />
                  <ArrowUpRight size={18} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </button>
              <button
                onClick={() => navigate(user ? "/account/profile" : "/login")}
                className="group flex items-center gap-5 py-4 text-left hover:pl-2 transition-[padding] duration-300"
                data-testid="hero-cta-signin"
              >
                <span className="font-serif text-[10px] tracking-[0.4em] uppercase text-neutral-500 w-6">02</span>
                <span className="flex-1 font-sans font-light text-[22px] text-white transition-all">
                  {t("vc.hero.cta-signin")}
                </span>
                <span className="flex items-center gap-2 text-neutral-500 group-hover:text-white transition-colors">
                  <span className="h-px w-8 bg-current transition-all group-hover:w-14" />
                  <ArrowUpRight size={18} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </button>
            </motion.div>

            {/* Bottom footnote strip — replaces the old page-index tag */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.42 }}
              className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] tracking-[0.35em] uppercase text-neutral-500"
            >
              <span>{t("vc.hero.foot-1")}</span>
              <span className="w-1 h-1 rounded-full bg-white/25" />
              <span>{t("vc.hero.foot-2")}</span>
              <span className="w-1 h-1 rounded-full bg-white/25" />
              <span>{t("vc.hero.foot-3")}</span>
            </motion.div>
          </div>

          {/* Right: floating tiles composition (static — no animation) */}
          <div
            className="lg:col-span-6 relative w-full h-[38svh] max-h-[380px] lg:h-[70vh] lg:max-h-[640px] hidden sm:block"
            data-testid="hero-parallax"
          >
            {/* Top-left slab */}
            <div className="absolute -left-4 top-6 w-[42%] max-w-[220px] aspect-[3/4] z-10">
              <FloatingTile className="w-full h-full" rotate={-14} edge="diagonal">
                <div className="h-full w-full flex flex-col justify-between p-5">
                  <span className="text-[9px] tracking-[0.4em] uppercase text-neutral-500">Business</span>
                  <span className="font-serif text-2xl text-white">Circle</span>
                </div>
              </FloatingTile>
            </div>

            {/* Top-right slab */}
            <div className="absolute right-0 top-2 w-[46%] max-w-[240px] aspect-[3/2] z-10">
              <FloatingTile className="w-full h-full" rotate={9} edge="top" variant="glass">
                <div className="h-full w-full flex items-center justify-between p-5">
                  <div>
                    <div className="text-[9px] tracking-[0.4em] uppercase text-neutral-500">Concierge</div>
                    <div className="font-serif text-lg text-white mt-2">24 · 7</div>
                  </div>
                  <div className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center text-white text-[10px]">·</div>
                </div>
              </FloatingTile>
            </div>

            {/* Bottom-left spotted tile */}
            <div className="absolute left-2 bottom-8 w-[46%] max-w-[240px] aspect-[3/2] z-10">
              <FloatingTile className="w-full h-full" rotate={7} edge="bottom" variant="spotted">
                <div className="h-full w-full flex items-end p-5">
                  <div>
                    <div className="text-[9px] tracking-[0.4em] uppercase text-neutral-500">Members</div>
                    <div className="font-serif text-lg text-white mt-1">Only</div>
                  </div>
                </div>
              </FloatingTile>
            </div>

            {/* Bottom-right slab */}
            <div className="absolute -right-4 bottom-4 w-[44%] max-w-[230px] aspect-[3/4] z-10">
              <FloatingTile className="w-full h-full" rotate={-10} edge="diagonal">
                <div className="h-full w-full flex flex-col justify-between p-5">
                  <div className="text-[9px] tracking-[0.4em] uppercase text-neutral-500">Since</div>
                  <div className="font-serif text-3xl text-white">2026</div>
                </div>
              </FloatingTile>
            </div>

            {/* Floating orbs (static) */}
            <div className="absolute left-[8%] bottom-[12%] w-16 h-16 rounded-full bg-gradient-to-br from-white/25 via-neutral-500 to-neutral-800 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.9),inset_-6px_-6px_20px_rgba(0,0,0,0.6),inset_6px_6px_20px_rgba(255,255,255,0.2)]" />
            <div className="absolute right-[14%] top-[38%] w-10 h-10 rounded-full bg-gradient-to-br from-white/20 via-neutral-500 to-neutral-800 shadow-[0_10px_20px_-6px_rgba(0,0,0,0.8),inset_-3px_-3px_10px_rgba(0,0,0,0.6),inset_3px_3px_10px_rgba(255,255,255,0.2)]" />
          </div>
        </div>

        {/* Bottom soft floor reflection */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black to-transparent" />
      </section>

      {/* ============ Membership Tiers ============ */}
      <section className="relative border-t border-white/[0.06] overflow-hidden">
        <VMark position="bottom-right" size={720} opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
          <motion.div {...fade}>
            <Eyebrow index="02">{t("vc.tiers.eyebrow")}</Eyebrow>
          </motion.div>

          <motion.h2
            {...fade}
            transition={{ ...fade.transition, delay: 0.08 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-8 max-w-[18ch] leading-[1.05]"
          >
            {t("vc.tiers.title")}
          </motion.h2>
          <motion.p
            {...fade}
            transition={{ ...fade.transition, delay: 0.14 }}
            className="mt-6 text-base lg:text-lg text-neutral-400 font-light leading-relaxed max-w-[58ch]"
          >
            {t("vc.tiers.sub")}
          </motion.p>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIERS.map((tier, i) => (
              <motion.div
                key={tier.id}
                {...fade}
                transition={{ ...fade.transition, delay: 0.06 * i }}
                className={`relative rounded-3xl p-7 flex flex-col border bg-[#0A0A0A] ${tier.popular ? "border-white/45 shadow-[0_0_40px_rgba(255,255,255,0.05)]" : "border-white/[0.08]"}`}
                data-testid={`tier-card-${tier.id}`}
              >
                {tier.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-black px-4 py-1 rounded-full text-[10px] tracking-[0.25em] uppercase font-medium whitespace-nowrap">
                    {t("vc.tiers.most-popular")}
                  </span>
                )}
                <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">{t(tier.tag)}</div>
                <h3 className="font-serif text-4xl mt-4">{tier.name}</h3>
                <div className="mt-2.5 flex items-baseline gap-1.5">
                  <span className="text-xl">{priceOf(tier, t)}</span>
                  {tier.period && <span className="text-[11px] text-neutral-500">{t(tier.period)}</span>}
                </div>
                <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed">{t(tier.body)}</p>

                <div className="my-5 h-px bg-white/[0.08]" />

                <ul className="space-y-2.5 flex-1">
                  {tier.features.map((f) => {
                    const Icon = FEATURE_ICONS[f] || Check;
                    const accent = FEATURE_ICONS[f] ? "text-[#E5484D]" : "text-white/50";
                    return (
                      <li key={f} className="flex items-start gap-2 text-[12px] text-neutral-300 font-light leading-relaxed">
                        <Icon size={13} strokeWidth={1.75} className={`mt-0.5 flex-shrink-0 ${accent}`} />
                        {t(f)}
                      </li>
                    );
                  })}
                </ul>

                <button
                  onClick={() => navigate("/membership")}
                  className={`mt-7 inline-flex items-center justify-between gap-2 px-5 py-3 rounded-full text-[11px] tracking-[0.25em] uppercase transition-colors ${
                    tier.popular
                      ? "bg-white text-black hover:bg-neutral-200"
                      : "border border-white/20 text-white hover:bg-white/5 hover:border-white/45"
                  }`}
                  data-testid={`tier-select-${tier.id}`}
                >
                  {tier.id === "corporate" ? t("vc.tiers.contact") : tier.id === "silver" ? t("vc.tiers.get-started") : t("vc.tiers.select")}
                  <ArrowRight size={14} />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ The Club ============ */}
      <section className="relative border-t border-white/[0.06] overflow-hidden bg-[#080808]">
        <VMark position="bottom-left" size={620} opacity={0.04} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
          <motion.div {...fade}>
            <Eyebrow index="03">{t("vc.club.eyebrow")}</Eyebrow>
          </motion.div>

          <motion.h2
            {...fade}
            transition={{ ...fade.transition, delay: 0.08 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-8 max-w-[18ch] leading-[1.05]"
          >
            {t("vc.club.title")}
          </motion.h2>

          <motion.p
            {...fade}
            transition={{ ...fade.transition, delay: 0.14 }}
            className="mt-8 text-base lg:text-lg text-neutral-400 font-light max-w-[62ch] leading-relaxed"
          >
            {t("vc.club.sub")}
          </motion.p>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-3xl overflow-hidden">
            {clubPillars.map((p, i) => (
              <motion.div
                key={p.n}
                {...fade}
                transition={{ ...fade.transition, delay: 0.08 * i }}
                className="bg-[#0A0A0A] p-8 lg:p-10 min-h-[280px] flex flex-col hover:bg-[#111] transition-colors"
                data-testid={`club-pillar-${p.n}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-serif text-4xl text-neutral-500">{p.n}</span>
                  <span className="text-[10px] tracking-[0.35em] uppercase text-neutral-600">{t("vc.club.pillar-tag")}</span>
                </div>
                <h3 className="font-serif text-2xl lg:text-3xl mt-8">{p.title}</h3>
                <p className="mt-5 text-sm text-neutral-400 font-light leading-relaxed flex-1">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Business Network ============ */}
      <section className="relative border-t border-white/[0.06] overflow-hidden">
        <VMark position="top-left" size={700} opacity={0.045} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <motion.div {...fade} className="lg:col-span-5">
              <Eyebrow index="04">{t("vc.net.eyebrow")}</Eyebrow>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-8 leading-[1.05]">
                {t("vc.net.title")}
              </h2>
              <p className="mt-8 text-base lg:text-lg text-neutral-400 font-light leading-relaxed max-w-[42ch]">
                {t("vc.net.sub")}
              </p>
            </motion.div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {network.map((c, i) => (
                <motion.div
                  key={c.n}
                  {...fade}
                  transition={{ ...fade.transition, delay: 0.06 * i }}
                  className="group relative bg-[#0E0E0E] border border-white/[0.08] rounded-2xl p-7 hover:border-white/25 hover:bg-[#141414] transition-all"
                  data-testid={`network-card-${c.n}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-serif text-xl text-neutral-500">{c.n}</span>
                    <ArrowUpRight size={16} className="text-neutral-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-serif text-2xl mt-6">{c.title}</h3>
                  <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed">{c.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ============ Concierge ============ */}
      <section className="relative border-t border-white/[0.06] overflow-hidden bg-[#080808]">
        <VMark position="top-right" size={640} opacity={0.045} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
          <motion.div {...fade}>
            <Eyebrow index="05">{t("vc.conc.eyebrow")}</Eyebrow>
          </motion.div>

          <motion.h2
            {...fade}
            transition={{ ...fade.transition, delay: 0.08 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-8 max-w-[20ch] leading-[1.05]"
          >
            {t("vc.conc.title")}
          </motion.h2>
          <motion.p
            {...fade}
            transition={{ ...fade.transition, delay: 0.14 }}
            className="mt-6 text-base lg:text-lg text-neutral-400 font-light leading-relaxed max-w-[62ch]"
          >
            {t("vc.conc.sub")}
          </motion.p>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              { title: t("vc.conc.you-set"), body: t("vc.conc.verbs") },
              { title: t("vc.conc.takes"), body: t("vc.conc.tasks") },
              { title: t("vc.conc.time"), body: t("vc.conc.time-body") },
            ].map((b, i) => (
              <motion.div
                key={i}
                {...fade}
                transition={{ ...fade.transition, delay: 0.06 * i }}
                className="rounded-3xl border border-white/[0.08] bg-[#0E0E0E] p-8 lg:p-10"
              >
                <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">{t("vc.concierge.step")} 0{i + 1}</div>
                <h3 className="font-serif text-2xl lg:text-3xl mt-4 leading-tight">{b.title}</h3>
                <p className="mt-6 text-sm text-neutral-400 font-light leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Lifestyle & Travel ============ */}
      <section className="relative border-t border-white/[0.06] overflow-hidden">
        <VMark position="bottom-left" size={720} opacity={0.04} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
          <motion.div {...fade}>
            <Eyebrow index="06">{t("vc.life.eyebrow")}</Eyebrow>
          </motion.div>

          <motion.h2
            {...fade}
            transition={{ ...fade.transition, delay: 0.08 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-8 max-w-[22ch] leading-[1.05]"
          >
            {t("vc.life.title")}
          </motion.h2>
          <motion.p
            {...fade}
            transition={{ ...fade.transition, delay: 0.14 }}
            className="mt-6 text-base lg:text-lg text-neutral-400 font-light leading-relaxed max-w-[58ch]"
          >
            {t("vc.life.sub")}
          </motion.p>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.06] rounded-3xl overflow-hidden">
            {lifestyle.map((s, i) => (
              <motion.div
                key={s.title}
                {...fade}
                transition={{ ...fade.transition, delay: 0.05 * i }}
                className="bg-[#0A0A0A] hover:bg-[#111] p-10 lg:p-12 min-h-[220px] transition-colors"
                data-testid={`lifestyle-${s.title}`}
              >
                <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">0{i + 1}</div>
                <h3 className="font-serif text-3xl mt-5">{s.title}</h3>
                <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed max-w-[52ch]">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Club Life ============ */}
      <section className="relative border-t border-white/[0.06] overflow-hidden bg-[#080808]">
        <VMark position="top-left" size={640} opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
          <motion.div {...fade}>
            <Eyebrow index="07">{t("vc.event.eyebrow")}</Eyebrow>
          </motion.div>
          <motion.h2
            {...fade}
            transition={{ ...fade.transition, delay: 0.08 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-8 max-w-[18ch] leading-[1.05]"
          >
            {t("vc.event.title")}
          </motion.h2>
          <motion.p
            {...fade}
            transition={{ ...fade.transition, delay: 0.14 }}
            className="mt-6 text-base lg:text-lg text-neutral-400 font-light leading-relaxed max-w-[58ch]"
          >
            {t("vc.event.sub")}
          </motion.p>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {events.map((e, i) => (
              <motion.div
                key={e.title}
                {...fade}
                transition={{ ...fade.transition, delay: 0.06 * i }}
                className="rounded-3xl border border-white/[0.08] bg-[#0E0E0E] p-8 hover:border-white/25 transition-colors"
              >
                <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">0{i + 1}</div>
                <h3 className="font-serif text-2xl lg:text-3xl mt-5">{e.title}</h3>
                <p className="mt-5 text-sm text-neutral-400 font-light leading-relaxed">{e.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ How to become a member ============ */}
      <section className="relative border-t border-white/[0.06] overflow-hidden bg-[#080808]" data-testid="how-to-join-section">
        <VMark position="top-right" size={720} opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <motion.div {...fade} className="max-w-3xl">
            <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.hj.eyebrow")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              {t("vc.hj.title-1")} <span className="italic text-neutral-300">{t("vc.hj.italic")}</span>
            </h2>
            <p className="mt-6 text-lg text-neutral-400 font-light leading-relaxed">
              {t("vc.hj.sub")}
            </p>
          </motion.div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { title: t("vc.hj.s1-title"), body: t("vc.hj.s1-body") },
              { title: t("vc.hj.s2-title"), body: t("vc.hj.s2-body") },
              { title: t("vc.hj.s3-title"), body: t("vc.hj.s3-body") },
              { title: t("vc.hj.s4-title"), body: t("vc.hj.s4-body") },
              { title: t("vc.hj.s5-title"), body: t("vc.hj.s5-body") },
            ].map((s, i) => (
              <motion.div
                key={i}
                {...fade}
                transition={{ ...fade.transition, delay: 0.06 * i }}
                className="relative rounded-3xl border border-white/[0.08] bg-[#0E0E0E] p-7 hover:border-white/25 hover:bg-[#141414] transition-colors flex flex-col"
                data-testid={`join-step-${i + 1}`}
              >
                <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">{t("vc.onb.step")} 0{i + 1}</div>
                <div className="font-serif text-6xl text-neutral-700 mt-3">0{i + 1}</div>
                <h3 className="font-serif text-xl mt-6">{s.title}</h3>
                <p className="mt-3 text-sm text-neutral-400 font-light leading-relaxed flex-1">{s.body}</p>
                {i < 4 && <div className="mt-6 h-px w-8 bg-white/20" />}
              </motion.div>
            ))}
          </div>

          <motion.div {...fade} className="mt-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <p className="text-sm text-neutral-400 font-light max-w-[48ch] leading-relaxed">{t("vc.hj.footnote")}</p>
            <button
              onClick={() => navigate(user ? "/onboarding" : "/register")}
              className="inline-flex items-center gap-3 bg-white text-black px-9 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
              data-testid="how-to-join-cta"
            >
              {user ? t("vc.hj.cta-signed") : t("vc.hj.cta")}
              <ArrowRight size={16} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ============ Final tagline ============ */}
      <section className="relative border-t border-white/[0.06] overflow-hidden">
        <VMark position="center" size={900} opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-32 lg:py-48 text-center">
          <motion.h2
            {...fade}
            className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[1.05]"
          >
            {t("vc.tagline.title-1")}<br />
            <span className="italic text-neutral-400">{t("vc.tagline.title-2")}</span><br />
            {t("vc.tagline.title-3")}
          </motion.h2>
        </div>
      </section>

      <VangardFooter />
    </div>
  );
}
